import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicQuiz = () => {
  const { shareToken } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSharedQuiz();
  }, [shareToken]);

  const fetchSharedQuiz = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/share/${shareToken}`);
      
      if (response.data.error) {
        setError(response.data.message);
      } else {
        setQuiz(response.data.quiz);
        setShareInfo(response.data.share_info);
      }
    } catch (error) {
      console.error('Failed to fetch shared quiz:', error);
      if (error.response?.status === 403) {
        setError(error.response.data.detail || 'Access denied. The quiz owner needs an active subscription.');
      } else if (error.response?.status === 404) {
        setError('Shared quiz not found or link is invalid.');
      } else {
        setError('Failed to load shared quiz. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading shared quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 border border-gray-200">
          <div className="text-center">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!quiz) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Shared Quiz
                </span>
                <span className="text-sm text-gray-500 capitalize">
                  {shareInfo?.access_mode === 'view' ? '🔒 Read Only' : '✏️ Can Attempt'}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mt-2">{quiz.title}</h1>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p className="capitalize">Difficulty: {quiz.difficulty}</p>
              <p>{quiz.questions.length} Questions</p>
            </div>
          </div>
        </div>
      </div>

      {/* All Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {quiz.questions.map((question, idx) => (
            <div
              key={question.id}
              className="bg-white rounded-xl shadow-sm p-8 border border-gray-200"
            >
              {/* Question Number and Type Badge */}
              <div className="flex items-center space-x-3 mb-4">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white font-bold text-sm">
                  {idx + 1}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 capitalize">
                  {question.type.replace('_', ' ')}
                </span>
              </div>

              {/* Question Text */}
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {question.question}
              </h2>

              {/* Answer Options */}
              {question.type === 'fill_blank' || question.type === 'short_answer' ? (
                <div className="p-4 bg-gray-50 border-2 border-gray-300 rounded-lg">
                  <span className="text-gray-500 italic">Answer: ________________________________</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {question.options?.map((option, optIdx) => (
                    <div
                      key={optIdx}
                      className="w-full text-left p-4 border-2 border-gray-300 rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-gray-400 mr-3 flex items-center justify-center">
                          <div className="w-2 h-2 bg-gray-300 rounded-full" />
                        </div>
                        <span className="text-gray-900">{option}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Explanation (if access mode allows) */}
              {shareInfo?.access_mode === 'view' && question.explanation && (
                <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-sm font-medium text-blue-800 mb-1">Explanation:</p>
                  <p className="text-sm text-blue-700">{question.explanation}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            This is a shared quiz. {shareInfo?.access_mode === 'view' ? 'View only mode.' : 'Login to attempt this quiz.'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            Login to Create Your Own Quiz
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicQuiz;
